import React, { useState, useEffect, useCallback } from 'react';
import { Save, Loader2, Sparkles, Key, MessageSquare, Users, AlertCircle, Clock, Activity, GitBranch, Send, ShoppingCart, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { aiConfigApi, topupApi } from './services/aiChatbotApi';
import { useToast } from '../email/shared/ToastProvider';
import './AIChatbot.css';

const PROVIDERS = [
  { value: 'gemini',    label: 'Google Gemini' },
  { value: 'openai',    label: 'OpenAI (GPT)' },
  { value: 'anthropic', label: 'Anthropic (Claude)' },
];

const MODEL_DEFAULTS = {
  gemini:    { chat: 'gemini-2.5-flash',     embedding: 'gemini-embedding-001' },
  openai:    { chat: 'gpt-4o-mini',          embedding: 'text-embedding-3-small' },
  anthropic: { chat: 'claude-3-5-sonnet-latest', embedding: 'text-embedding-3-small' }, // anthropic has no embed; falls back
};

const AIChatbotConfig = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [skills, setSkills]   = useState([]);
  const [usage, setUsage]     = useState(null);
  const [keywordInput, setKeywordInput] = useState('');

  // Phase 9-11 — routing test + topup plans
  const [testInput, setTestInput] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [topupPlans, setTopupPlans] = useState([]);
  const [buying, setBuying] = useState(null);

  const [form, setForm] = useState({
    is_enabled: false,
    skill_key: 'ecommerce',
    skill_keys: [],
    provider: 'gemini',
    chat_model: 'gemini-2.5-flash',
    embedding_model: 'gemini-embedding-001',
    custom_system_prompt: '',
    fallback_message: 'Sorry, I could not find an answer. Connecting you to a human agent.',
    handoff_keywords: ['agent', 'human', 'support'],
    credits_total: 1000,
    // Phase 5/6/7/8 — routing, idle, token budget
    routing_order: ['bot', 'ai', 'human'],
    idle_nudge_minutes: 5,
    idle_close_minutes: 5,
    idle_nudge_message: 'Are you still there? Reply to keep the conversation going.',
    idle_close_message: 'No worries — closing this conversation. Reply anytime to start over.',
    monthly_token_budget: 0,
    overage_policy: 'block',
  });

  /* ── Initial load ───────────────────────────────────── */
  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const [skillsRes, cfgRes, usageRes] = await Promise.all([
        aiConfigApi.listSkills(),
        aiConfigApi.getConfig(),
        aiConfigApi.getUsage(),
      ]);

      if (skillsRes.data?.entity) {
        // Phase 9: backend returns { all, custom, templates }. Show ONLY the
        // customer's own custom skills here — starter templates live on the
        // "My Skills" tab where they can be cloned.
        const entity = skillsRes.data.entity;
        if (Array.isArray(entity)) {
          setSkills(entity);
        } else {
          setSkills(entity.custom || []);
        }
      }

      if (cfgRes.data?.entity) {
        const c = cfgRes.data.entity;
        setForm({
          is_enabled: !!c.is_enabled,
          skill_key: c.skill_key || 'ecommerce',
          skill_keys: Array.isArray(c.skill_keys) ? c.skill_keys : [],
          provider: c.provider || 'gemini',
          chat_model: c.chat_model || 'gemini-2.5-flash',
          embedding_model: c.embedding_model || 'gemini-embedding-001',
          custom_system_prompt: c.custom_system_prompt || '',
          fallback_message: c.fallback_message || '',
          handoff_keywords: Array.isArray(c.handoff_keywords) ? c.handoff_keywords : [],
          credits_total: c.credits_total || 1000,
          routing_order: Array.isArray(c.routing_order) && c.routing_order.length
            ? c.routing_order
            : ['bot', 'ai', 'human'],
          idle_nudge_minutes: c.idle_nudge_minutes ?? 5,
          idle_close_minutes: c.idle_close_minutes ?? 5,
          idle_nudge_message: c.idle_nudge_message || 'Are you still there? Reply to keep the conversation going.',
          idle_close_message: c.idle_close_message || 'No worries — closing this conversation. Reply anytime to start over.',
          monthly_token_budget: Number(c.monthly_token_budget) || 0,
          overage_policy: c.overage_policy || 'block',
        });
      }

      if (usageRes.data?.entity) setUsage(usageRes.data.entity);
    } catch (err) {
      console.error('AI config load error:', err);
      toast.error('Failed to load chatbot configuration');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Phase 10 — load topup plans (independent of config load failure)
  useEffect(() => {
    topupApi.listPlans()
      .then((r) => {
        const list = r.data?.entity || r.data?.data || r.data || [];
        const arr = Array.isArray(list) ? list : (list.plans || []);
        setTopupPlans(arr.filter((p) => Number(p.ai_token_credits || 0) > 0));
      })
      .catch(() => setTopupPlans([]));
  }, []);

  /* ── Routing test (Phase 7) ─────────────────────────── */
  const runRoutingTest = async () => {
    if (!testInput.trim()) return;
    try {
      setTesting(true);
      setTestResult(null);
      const res = await aiConfigApi.testRouting(testInput.trim());
      setTestResult(res.data?.entity || res.data || null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Routing test failed');
    } finally {
      setTesting(false);
    }
  };

  /* ── Buy tokens (Phase 10) ──────────────────────────── */
  const buyTopup = async (plan) => {
    try {
      setBuying(plan.id);
      const order = await topupApi.createOrder({
        topupPlanId: plan.id,
        amount: plan.price_inr || plan.price,
        currency: 'INR',
      });
      const o = order.data?.entity || order.data;
      if (!window.Razorpay) {
        toast.error('Razorpay SDK not loaded');
        return;
      }
      const rzp = new window.Razorpay({
        key: o.razorpay_key,
        order_id: o.order_id,
        amount: o.amount,
        currency: o.currency || 'INR',
        name: 'MettaMate AI Tokens',
        description: `${plan.name} — ${Number(plan.ai_token_credits).toLocaleString()} tokens`,
        handler: async (rp) => {
          try {
            await topupApi.verifyPayment({
              razorpay_payment_id: rp.razorpay_payment_id,
              razorpay_order_id:   rp.razorpay_order_id,
              razorpay_signature:  rp.razorpay_signature,
              topupPlanId:         plan.id,
            });
            toast.success('Tokens credited');
            await loadAll();
          } catch (e) {
            toast.error('Payment verification failed');
          }
        },
        theme: { color: '#4f46e5' },
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not start checkout');
    } finally {
      setBuying(null);
    }
  };

  /* ── Helpers ────────────────────────────────────────── */
  const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const onProviderChange = (provider) => {
    const defaults = MODEL_DEFAULTS[provider] || MODEL_DEFAULTS.gemini;
    setForm((f) => ({
      ...f,
      provider,
      chat_model: defaults.chat,
      embedding_model: defaults.embedding,
    }));
  };

  const addKeyword = () => {
    const v = keywordInput.trim().toLowerCase();
    if (!v) return;
    if (form.handoff_keywords.includes(v)) { setKeywordInput(''); return; }
    setForm((f) => ({ ...f, handoff_keywords: [...f.handoff_keywords, v] }));
    setKeywordInput('');
  };

  const removeKeyword = (kw) => {
    setForm((f) => ({ ...f, handoff_keywords: f.handoff_keywords.filter((k) => k !== kw) }));
  };

  const onSave = async () => {
    try {
      setSaving(true);
      const res = await aiConfigApi.updateConfig(form);
      if (res.data?.statuscode === 200 || res.data?.status) {
        toast.success('Configuration saved');
        if (res.data.entity) setForm((f) => ({ ...f, ...res.data.entity, handoff_keywords: res.data.entity.handoff_keywords || f.handoff_keywords }));
      } else {
        toast.error(res.data?.message || 'Failed to save');
      }
    } catch (err) {
      console.error('AI config save error:', err);
      toast.error(err.response?.data?.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="ai-loader">
        <Loader2 size={24} className="spin" /> &nbsp; Loading configuration…
      </div>
    );
  }

  /* ── Usage bar styling ──────────────────────────────── */
  const used = usage?.creditsUsed ?? 0;
  const total = usage?.creditsTotal ?? form.credits_total ?? 1000;
  const pct = Math.min(100, Math.round((used / Math.max(total, 1)) * 100));
  const usageClass = pct > 90 ? 'danger' : pct > 70 ? 'warn' : '';

  return (
    <>
      {/* Stats */}
      <div className="ai-stats">
        <div className="ai-stat">
          <div className="ai-stat-label">Status</div>
          <div className="ai-stat-value" style={{ color: form.is_enabled ? '#16a34a' : '#94a3b8' }}>
            {form.is_enabled ? 'Active' : 'Disabled'}
          </div>
          <div className="ai-stat-sub">{form.provider} • {form.chat_model}</div>
        </div>
        <div className="ai-stat">
          <div className="ai-stat-label">Credits Used</div>
          <div className="ai-stat-value">{used} <span style={{ fontSize: 14, color: '#94a3b8' }}>/ {total}</span></div>
          <div className="ai-usage-bar">
            <div className={`ai-usage-fill ${usageClass}`} style={{ width: `${pct}%` }} />
          </div>
        </div>
        <div className="ai-stat">
          <div className="ai-stat-label">Skill</div>
          <div className="ai-stat-value" style={{ fontSize: 18 }}>
            {skills.find((s) => s.skill_key === form.skill_key)?.display_name || form.skill_key}
          </div>
          <div className="ai-stat-sub">Resets monthly on 1st</div>
        </div>
      </div>

      {/* Enable / Disable */}
      <div className="ai-card">
        <div className="ai-card-header">
          <div>
            <h3 className="ai-card-title">Bot Status</h3>
            <p className="ai-card-subtitle">Turn the AI assistant on or off for your WhatsApp Business number.</p>
          </div>
          <label className="ai-toggle">
            <span className="ai-toggle-label">{form.is_enabled ? 'Enabled' : 'Disabled'}</span>
            <span className={'ai-toggle-track' + (form.is_enabled ? ' on' : '')}>
              <span className="ai-toggle-thumb" />
            </span>
            <input
              type="checkbox"
              checked={form.is_enabled}
              onChange={(e) => setField('is_enabled', e.target.checked)}
              style={{ display: 'none' }}
            />
          </label>
        </div>
      </div>

      {/* Skill + Model */}
      <div className="ai-card">
        <h3 className="ai-card-title"><Sparkles size={16} style={{ display: 'inline', marginRight: 6, color: '#4f46e5' }} /> AI Skill & Model</h3>
        <p className="ai-card-subtitle" style={{ marginBottom: 16 }}>
          Choose a domain skill and the LLM provider. Each skill comes with a tuned system prompt.
        </p>

        <div className="ai-form-group">
          <label className="ai-label">Skills <span style={{ color: '#94a3b8', fontWeight: 400 }}>(stack one or more)</span></label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
            {skills.map((s) => {
              const checked = (form.skill_keys || []).includes(s.skill_key);
              return (
                <label
                  key={s.skill_key}
                  title={s.description || ''}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '6px 10px', borderRadius: 999, cursor: 'pointer',
                    border: `1px solid ${checked ? '#4f46e5' : '#e2e8f0'}`,
                    background: checked ? '#eef2ff' : '#fff',
                    color: checked ? '#3730a3' : '#334155',
                    fontSize: 13, fontWeight: 500,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => {
                      const cur = Array.isArray(form.skill_keys) ? form.skill_keys : [];
                      const next = e.target.checked
                        ? Array.from(new Set([...cur, s.skill_key]))
                        : cur.filter((k) => k !== s.skill_key);
                      setField('skill_keys', next);
                      // Keep legacy single-skill in sync (first selected) for back-compat.
                      setField('skill_key', next[0] || form.skill_key);
                    }}
                    style={{ accentColor: '#4f46e5' }}
                  />
                  {s.display_name}
                  {s.category && (
                    <span style={{ fontSize: 11, color: '#64748b', marginLeft: 2 }}>· {s.category}</span>
                  )}
                </label>
              );
            })}
            {skills.length === 0 && (
              <div
                style={{
                  padding: 16, background: '#f8fafc', borderRadius: 8,
                  border: '1px dashed #cbd5e1', width: '100%', textAlign: 'center',
                }}
              >
                <div style={{ color: '#475569', fontSize: 14, marginBottom: 8 }}>
                  You haven't created any skills yet.
                </div>
                <Link
                  to="/dashboard/ai-chatbot/skills"
                  className="ai-btn ai-btn-primary"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}
                >
                  <Plus size={14} /> Create or clone a skill
                </Link>
              </div>
            )}
          </div>
          <div className="ai-help">
            Stack one or more of <b>your own skills</b>. Each is a set of instructions
            you wrote in <Link to="/dashboard/ai-chatbot/skills">My Skills</Link>. The AI
            combines them at retrieval time using your knowledge base.
          </div>
        </div>

        <div className="ai-form-row">
          <div className="ai-form-group">
            <label className="ai-label">Provider</label>
            <select
              className="ai-select"
              value={form.provider}
              onChange={(e) => onProviderChange(e.target.value)}
            >
              {PROVIDERS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>

          <div className="ai-form-group" />
        </div>

        <div className="ai-form-row">
          <div className="ai-form-group">
            <label className="ai-label">Chat Model</label>
            <input
              className="ai-input"
              value={form.chat_model}
              onChange={(e) => setField('chat_model', e.target.value)}
              placeholder="e.g. gemini-2.5-flash"
            />
            <div className="ai-help">Default for {form.provider}: {MODEL_DEFAULTS[form.provider]?.chat}</div>
          </div>
          <div className="ai-form-group">
            <label className="ai-label">Embedding Model</label>
            <input
              className="ai-input"
              value={form.embedding_model}
              onChange={(e) => setField('embedding_model', e.target.value)}
              placeholder="e.g. gemini-embedding-001"
            />
            <div className="ai-help">Used to embed knowledge-base chunks for retrieval.</div>
          </div>
        </div>
      </div>

      {/* Prompts & messages */}
      <div className="ai-card">
        <h3 className="ai-card-title"><MessageSquare size={16} style={{ display: 'inline', marginRight: 6, color: '#4f46e5' }} /> Messages & Prompt</h3>

        <div className="ai-form-group">
          <label className="ai-label">Custom System Prompt <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
          <textarea
            className="ai-textarea"
            rows={4}
            value={form.custom_system_prompt}
            onChange={(e) => setField('custom_system_prompt', e.target.value)}
            placeholder="Override the default skill prompt with your own instructions. Leave blank to use the skill default."
          />
        </div>

        <div className="ai-form-group">
          <label className="ai-label">Fallback Message</label>
          <input
            className="ai-input"
            value={form.fallback_message}
            onChange={(e) => setField('fallback_message', e.target.value)}
          />
          <div className="ai-help">Shown when the AI cannot answer or is handing off to a human.</div>
        </div>
      </div>

      {/* Handoff keywords */}
      <div className="ai-card">
        <h3 className="ai-card-title"><Users size={16} style={{ display: 'inline', marginRight: 6, color: '#4f46e5' }} /> Human Handoff</h3>
        <p className="ai-card-subtitle" style={{ marginBottom: 12 }}>
          When a customer types any of these keywords, the bot pauses and routes the chat to a human agent.
        </p>

        <div className="ai-tags">
          {form.handoff_keywords.map((kw) => (
            <span key={kw} className="ai-tag">
              {kw}
              <button className="ai-tag-remove" onClick={() => removeKeyword(kw)} aria-label={`remove ${kw}`}>×</button>
            </span>
          ))}
          <input
            className="ai-tag-input"
            placeholder="Type a keyword and press Enter…"
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addKeyword(); }
              if (e.key === 'Backspace' && !keywordInput && form.handoff_keywords.length) {
                removeKeyword(form.handoff_keywords[form.handoff_keywords.length - 1]);
              }
            }}
          />
        </div>
      </div>

      {/* Credit limit */}
      <div className="ai-card">
        <h3 className="ai-card-title"><Key size={16} style={{ display: 'inline', marginRight: 6, color: '#4f46e5' }} /> Monthly Credit Limit</h3>
        <p className="ai-card-subtitle" style={{ marginBottom: 12 }}>
          One credit = one AI reply. Once exhausted, the bot will use the fallback message until the next reset.
        </p>
        <div className="ai-form-group" style={{ maxWidth: 240 }}>
          <input
            type="number"
            min="0"
            className="ai-input"
            value={form.credits_total}
            onChange={(e) => setField('credits_total', parseInt(e.target.value || '0', 10))}
          />
        </div>
      </div>

      {/* Routing pipeline */}
      <div className="ai-card">
        <h3 className="ai-card-title">
          <GitBranch size={16} style={{ display: 'inline', marginRight: 6, color: '#4f46e5' }} />
          Routing Pipeline
        </h3>
        <p className="ai-card-subtitle" style={{ marginBottom: 12 }}>
          Order in which incoming messages are tried. The first stage that handles
          the message wins. <code>human</code> always terminates the pipeline.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          {(form.routing_order || []).map((stage, idx) => (
            <span
              key={`${stage}-${idx}`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '6px 12px', borderRadius: 8,
                background: stage === 'bot' ? '#dbeafe' : stage === 'ai' ? '#ede9fe' : '#fee2e2',
                color:      stage === 'bot' ? '#1e40af' : stage === 'ai' ? '#5b21b6' : '#991b1b',
                fontWeight: 600, fontSize: 13,
              }}
            >
              {idx + 1}. {stage}
              <button
                type="button"
                onClick={() => {
                  const next = [...form.routing_order];
                  if (idx > 0) { [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]]; setField('routing_order', next); }
                }}
                disabled={idx === 0}
                style={{ background: 'transparent', border: 'none', cursor: idx === 0 ? 'not-allowed' : 'pointer', opacity: idx === 0 ? 0.3 : 1, padding: 0 }}
                aria-label="move up"
              >↑</button>
              <button
                type="button"
                onClick={() => {
                  const next = [...form.routing_order];
                  if (idx < next.length - 1) { [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]]; setField('routing_order', next); }
                }}
                disabled={idx === (form.routing_order?.length || 0) - 1}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', opacity: idx === (form.routing_order?.length || 0) - 1 ? 0.3 : 1, padding: 0 }}
                aria-label="move down"
              >↓</button>
              <button
                type="button"
                onClick={() => {
                  const next = form.routing_order.filter((_, i) => i !== idx);
                  setField('routing_order', next.length ? next : ['ai']);
                }}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0 }}
                aria-label={`remove ${stage}`}
              >×</button>
            </span>
          ))}
          {['bot', 'ai', 'human'].filter((s) => !(form.routing_order || []).includes(s)).map((s) => (
            <button
              key={s}
              type="button"
              className="ai-btn ai-btn-secondary"
              style={{ padding: '4px 10px', fontSize: 12 }}
              onClick={() => setField('routing_order', [...(form.routing_order || []), s])}
            >
              + {s}
            </button>
          ))}
        </div>
        <div className="ai-help" style={{ marginTop: 10 }}>
          Default: <b>bot → ai → human</b>. Disable a stage by removing its chip.
        </div>
      </div>

      {/* Routing test */}
      {/* <div className="ai-card">
        <h3 className="ai-card-title">
          <Send size={16} style={{ display: 'inline', marginRight: 6, color: '#4f46e5' }} />
          Test Routing
        </h3>
        <p className="ai-card-subtitle" style={{ marginBottom: 12 }}>
          Simulate an incoming message and see exactly which stage of your pipeline handles it.
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            className="ai-input"
            style={{ flex: 1 }}
            placeholder="e.g. What sizes do you have in stock?"
            value={testInput}
            onChange={(e) => setTestInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') runRoutingTest(); }}
          />
          <button className="ai-btn ai-btn-primary" onClick={runRoutingTest} disabled={testing || !testInput.trim()}>
            {testing ? <Loader2 size={14} className="spin" /> : <Send size={14} />} Run
          </button>
        </div>
        {testResult && (
          <div style={{ marginTop: 14, padding: 12, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: 13, marginBottom: 8 }}>
              Stage that handled the message:&nbsp;
              <span
                style={{
                  display: 'inline-block', padding: '2px 10px', borderRadius: 999,
                  background: testResult.handledBy === 'bot' ? '#dbeafe' : testResult.handledBy === 'ai' ? '#ede9fe' : '#fee2e2',
                  color:      testResult.handledBy === 'bot' ? '#1e40af' : testResult.handledBy === 'ai' ? '#5b21b6' : '#991b1b',
                  fontWeight: 700,
                }}
              >
                {testResult.handledBy || 'none'}
              </span>
            </div>
            {testResult.reply && (
              <div style={{ fontSize: 14, color: '#0f172a', whiteSpace: 'pre-wrap' }}>
                <b>Reply preview:</b><br />{testResult.reply}
              </div>
            )}
            {Array.isArray(testResult.trace) && testResult.trace.length > 0 && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
                <b>Trace:</b>
                <ul style={{ margin: '4px 0 0 16px' }}>
                  {testResult.trace.map((t, i) => <li key={i}>{t}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </div> */}

      {/* Idle handling */}
      <div className="ai-card">
        <h3 className="ai-card-title">
          <Clock size={16} style={{ display: 'inline', marginRight: 6, color: '#4f46e5' }} />
          Idle Handling
        </h3>
        <p className="ai-card-subtitle" style={{ marginBottom: 12 }}>
          Send a gentle nudge after a quiet period, then auto-close the session if there's still no reply.
        </p>
        <div className="ai-form-row">
          <div className="ai-form-group">
            <label className="ai-label">Nudge after (minutes idle)</label>
            <input
              type="number" min="0" max="1440"
              className="ai-input"
              value={form.idle_nudge_minutes}
              onChange={(e) => setField('idle_nudge_minutes', parseInt(e.target.value || '0', 10))}
            />
            <div className="ai-help">0 disables nudges.</div>
          </div>
          <div className="ai-form-group">
            <label className="ai-label">Auto-close after nudge (minutes)</label>
            <input
              type="number" min="0" max="1440"
              className="ai-input"
              value={form.idle_close_minutes}
              onChange={(e) => setField('idle_close_minutes', parseInt(e.target.value || '0', 10))}
            />
            <div className="ai-help">0 keeps the session open indefinitely.</div>
          </div>
        </div>
        <div className="ai-form-group">
          <label className="ai-label">Nudge message</label>
          <input
            className="ai-input"
            value={form.idle_nudge_message}
            onChange={(e) => setField('idle_nudge_message', e.target.value)}
          />
        </div>
        <div className="ai-form-group">
          <label className="ai-label">Auto-close message</label>
          <input
            className="ai-input"
            value={form.idle_close_message}
            onChange={(e) => setField('idle_close_message', e.target.value)}
          />
        </div>
      </div>

      {/* Token budget */}
      <div className="ai-card">
        <h3 className="ai-card-title">
          <Activity size={16} style={{ display: 'inline', marginRight: 6, color: '#4f46e5' }} />
          Token Budget &amp; Usage
        </h3>
        <p className="ai-card-subtitle" style={{ marginBottom: 12 }}>
          Hard cap on LLM tokens consumed per billing period. Plan upgrades and topups update this automatically.
        </p>

        {usage && (() => {
          const budget = Number(usage.monthlyTokenBudget) || 0;
          const usedT  = Number(usage.tokensUsedThisPeriod) || 0;
          const pctT   = budget > 0 ? Math.min(100, Math.round((usedT / budget) * 100)) : 0;
          const cls    = pctT >= 100 ? 'over' : pctT >= 80 ? 'high' : 'ok';
          return (
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                <span><b>{usedT.toLocaleString()}</b> tokens used</span>
                <span style={{ color: '#64748b' }}>
                  {budget > 0 ? `of ${budget.toLocaleString()} (${pctT}%)` : 'unlimited'}
                </span>
              </div>
              {budget > 0 && (
                <div className="ai-usage-bar">
                  <div className={`ai-usage-fill ${cls}`} style={{ width: `${pctT}%` }} />
                </div>
              )}
              {usage.periodEnd && (
                <div className="ai-help" style={{ marginTop: 6 }}>
                  Period ends: {new Date(usage.periodEnd).toLocaleDateString()}
                </div>
              )}
            </div>
          );
        })()}

        <div className="ai-form-row">
          <div className="ai-form-group">
            <label className="ai-label">Monthly token budget</label>
            <input
              type="number" min="0" step="1000"
              className="ai-input"
              value={form.monthly_token_budget}
              onChange={(e) => setField('monthly_token_budget', parseInt(e.target.value || '0', 10))}
            />
            <div className="ai-help">0 = unlimited (rare).</div>
          </div>
          <div className="ai-form-group">
            <label className="ai-label">Overage policy</label>
            <select
              className="ai-select"
              value={form.overage_policy}
              onChange={(e) => setField('overage_policy', e.target.value)}
            >
              <option value="block">Block — pause AI replies when budget hits</option>
              <option value="fallback_to_cheap_model">Fallback to cheap model when budget hits</option>
              <option value="topup">Allow overage (topup will cover)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Buy tokens (Phase 10) */}
      <div className="ai-card">
        <h3 className="ai-card-title">
          <ShoppingCart size={16} style={{ display: 'inline', marginRight: 6, color: '#4f46e5' }} />
          Buy More Tokens
        </h3>
        <p className="ai-card-subtitle" style={{ marginBottom: 12 }}>
          Topup tokens are added on top of your monthly plan budget and never expire.
        </p>
        {topupPlans.length === 0 ? (
          <div className="ai-help">No topup plans available right now.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
            {topupPlans.map((p) => (
              <div key={p.id} style={{ padding: 14, border: '1px solid #e2e8f0', borderRadius: 10, background: '#fafbff' }}>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{p.name}</div>
                <div style={{ color: '#4f46e5', fontWeight: 700, fontSize: 22 }}>
                  ₹{Number(p.price_inr || p.price || 0).toLocaleString()}
                </div>
                <div style={{ color: '#475569', fontSize: 13, margin: '6px 0' }}>
                  <b>{Number(p.ai_token_credits).toLocaleString()}</b> tokens
                </div>
                {p.description && <div style={{ color: '#64748b', fontSize: 12, marginBottom: 8 }}>{p.description}</div>}
                <button
                  className="ai-btn ai-btn-primary"
                  style={{ width: '100%' }}
                  disabled={buying === p.id}
                  onClick={() => buyTopup(p)}
                >
                  {buying === p.id ? <Loader2 size={14} className="spin" /> : <ShoppingCart size={14} />} Buy now
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Save bar */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <button className="ai-btn ai-btn-secondary" onClick={loadAll} disabled={saving}>
          Reset
        </button>
        <button className="ai-btn ai-btn-primary" onClick={onSave} disabled={saving}>
          {saving ? <><Loader2 size={14} className="spin" /> Saving…</> : <><Save size={14} /> Save Changes</>}
        </button>
      </div>

      {!form.is_enabled && (
        <div style={{ marginTop: 16, padding: 12, background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <AlertCircle size={18} color="#92400e" style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: 13, color: '#92400e' }}>
            The AI chatbot is currently <b>disabled</b>. Customer messages will continue through your existing bot flows or go to the inbox unanswered.
          </div>
        </div>
      )}
    </>
  );
};

export default AIChatbotConfig;
